import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth-guard';
import { demoGuard } from './core/guards/demo-guard';
import { subscriptionGuard } from './core/guards/subscription-guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'tabs/home',
    pathMatch: 'full',
  },
  // Subscription (no tabs, full-screen)
  {
    path: 'subscription',
    loadComponent: () => import('./pages/subscription/subscription.page').then(m => m.SubscriptionPage)
  },
  // Auth pages (no tabs)
  {
    path: 'pages/auth/login',
    loadComponent: () => import('./pages/auth/login/login.page').then(m => m.LoginPage)
  },
  {
    path: 'pages/auth/register',
    loadComponent: () => import('./pages/auth/register/register.page').then(m => m.RegisterPage)
  },
  {
    path: 'pages/auth/onboarding',
    loadComponent: () => import('./pages/auth/onboarding/onboarding.page').then(m => m.OnboardingPage)
  },
  {
    path: 'pages/auth/forgot-password',
    loadComponent: () => import('./pages/auth/forgot-password/forgot-password.page').then(m => m.ForgotPasswordPage)
  },
  {
    path: 'pages/auth/verify-email',
    loadComponent: () => import('./pages/auth/verify-email/verify-email.page').then(m => m.VerifyEmailPage)
  },
  // Legal — publicly accessible (no auth required)
  {
    path: 'legal',
    loadComponent: () => import('./pages/legal/legal.page').then(m => m.LegalPage)
  },
  // Challenge invite — publicly accessible. Param is an opaque share token;
  // the quiz is resolved via the getSharedContent Cloud Function (no DB read).
  {
    path: 'challenge-invite/:token',
    loadComponent: () => import('./pages/challenge-invite/challenge-invite.page').then(m => m.ChallengeInvitePage)
  },
  // Story invite — publicly accessible, no auth required
  {
    path: 'story/:storyId',
    loadComponent: () => import('./pages/story-invite/story-invite.page').then(m => m.StoryInvitePage)
  },
  // Room — publicly accessible (anonymous join supported)
  {
    path: 'room/:id',
    loadComponent: () => import('./pages/room/room.page').then(m => m.RoomPage)
  },
  // Friend invite — publicly accessible; page self-gates to a full account
  {
    path: 'add-friend/:token',
    loadComponent: () => import('./pages/add-friend/add-friend.page').then(m => m.AddFriendPage)
  },
  // Shared content (quiz/story/leader) via opaque token — publicly accessible.
  // Resolves through the getSharedContent Cloud Function; no blanket DB read.
  {
    path: 's/:type/:token',
    loadComponent: () => import('./pages/shared-content/shared-content.page').then(m => m.SharedContentPage)
  },
  // Main app with tabs (protected)
  {
    path: 'tabs',
    loadComponent: () => import('./pages/tabs/tabs/tabs.component').then(m => m.TabsComponent),
    canActivate: [authGuard],
    children: [
      {
        path: 'home',
        loadComponent: () => import('./home/home.page').then(m => m.HomePage)
      },
      {
        path: 'library',
        loadComponent: () => import('./pages/tabs/library/library.page').then(m => m.LibraryPage)
      },
      {
        path: 'explore',
        loadComponent: () => import('./pages/tabs/explore/explore.page').then(m => m.ExplorePage)
      },
      {
        path: 'social',
        canActivate: [demoGuard],
        loadComponent: () => import('./pages/tabs/social/social.page').then(m => m.SocialPage)
      },
      {
        path: 'profile',
        canActivate: [demoGuard],
        loadComponent: () => import('./pages/tabs/profile/profile.page').then(m => m.ProfilePage)
      },
      {
        path: 'backers',
        loadComponent: () => import('./pages/tabs/backers/backers.page').then(m => m.BackersPage)
      },
      {
        path: 'quizzes',
        loadComponent: () => import('./pages/tabs/quizzes/quizzes.page').then(m => m.QuizzesPage)
      },
      {
        path: 'leaders',
        loadComponent: () => import('./pages/tabs/leaders/leaders.page').then(m => m.LeadersPage)
      },
      {
        path: 'leader/:id',
        loadComponent: () => import('./pages/tabs/leader-detail/leader-detail.page').then(m => m.LeaderDetailPage)
      },
      {
        path: 'leader-items',
        loadComponent: () => import('./pages/tabs/leader-items/leader-items.page').then(m => m.LeaderItemsPage)
      },
      {
        path: 'video/:id',
        // canActivate: [subscriptionGuard], // TODO: re-enable when payments are live
        loadComponent: () => import('./pages/tabs/video-player/video-player.page').then(m => m.VideoPlayerPage)
      },
      {
        path: 'quiz/:id',
        // canActivate: [subscriptionGuard], // TODO: re-enable when payments are live
        loadComponent: () => import('./pages/tabs/quiz-play/quiz-play.page').then(m => m.QuizPlayPage)
      },
      {
        path: 'quizzes-catalog',
        loadComponent: () => import('./pages/tabs/quizzes-catalog/quizzes-catalog.page').then(m => m.QuizzesCatalogPage)
      },
      {
        path: 'search',
        loadComponent: () => import('./pages/tabs/search/search.page').then(m => m.SearchPage)
      },
      {
        path: 'challenge/:id',
        canActivate: [demoGuard],
        loadComponent: () => import('./pages/tabs/challenge-detail/challenge-detail.page').then(m => m.ChallengeDetailPage)
      },
      {
        path: 'billing',
        canActivate: [demoGuard],
        loadComponent: () => import('./pages/tabs/billing/billing.page').then(m => m.BillingPage)
      },
      {
        path: 'rooms',
        loadComponent: () => import('./pages/tabs/rooms/rooms.page').then(m => m.RoomsPage)
      },
      {
        path: 'notifications',
        loadComponent: () => import('./pages/tabs/notifications/notifications.page').then(m => m.NotificationsPage)
      },
      {
        path: 'notification-detail',
        loadComponent: () => import('./pages/tabs/notification-detail/notification-detail.page').then(m => m.NotificationDetailPage)
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
      }
    ]
  },
  // Legacy redirects
  {
    path: 'home',
    redirectTo: 'tabs/home',
    pathMatch: 'full'
  },
  {
    path: 'login',
    redirectTo: 'pages/auth/login',
    pathMatch: 'full'
  }
];
